import { Component, Host, h, Event, EventEmitter, Method, Element, Prop, Watch, AttachInternals } from '@stencil/core';
import { hasSlot } from '../../utils';
import 'element-internals-polyfill';

/**
 * @slot label - The label's text.
 *
 * @part input-group - The component's input-group element which wraps input and label.
 * @part input - The component's input element.
 * @part label - The component's label element.
 * @part suffix - The component's suffix element for icons.
 */
@Component({
  tag: 'o2-textarea',
  styleUrl: 'textarea.scss',
  shadow: true,
  formAssociated: true,
})
export class O2Textarea {
  private textarea?: HTMLTextAreaElement;
  private inputId = `o2-textarea-${textareaIds++}`;

  private hasLabelSlot: boolean = false;

  @AttachInternals() internals: ElementInternals;

  @Element() el!: HTMLO2TextareaElement;

  /**
   * Indicates error
   */
  @Prop({
    reflect: true,
  })
  error?: boolean = false;

  /**
   * Indicates warning
   */
  @Prop({
    reflect: true,
  })
  warning?: boolean = false;

  /**
   * Indicates whether and how the text value should be automatically capitalized as it is entered/edited by the user.
   */
  @Prop() autocapitalize? = 'none';

  /**
   * This Boolean attribute lets you specify that a form control should have input focus when the page loads.
   */
  @Prop() autofocus? = false;

  /**
   * If `true`, the user cannot interact with the textarea.
   */
  @Prop({
    reflect: true,
  })
  disabled? = false;

  /**
   * A hint to the browser for which keyboard to display.
   * Possible values: `"none"`, `"text"`, `"tel"`, `"url"`,
   * `"email"`, `"numeric"`, `"decimal"`, and `"search"`.
   */
  @Prop() inputmode?: 'none' | 'text' | 'tel' | 'url' | 'email' | 'numeric' | 'decimal' | 'search';

  /**
   * A hint to the browser for which enter key to display.
   * Possible values: `"enter"`, `"done"`, `"go"`, `"next"`,
   * `"previous"`, `"search"`, and `"send"`.
   */
  @Prop() enterkeyhint?: 'enter' | 'done' | 'go' | 'next' | 'previous' | 'search' | 'send';

  /**
   * If the value of the type attribute is `text`, `email`, `search`, `password`, `tel`, or `url`, this attribute specifies the maximum number of characters that the user can enter.
   */
  @Prop() maxlength?: number;

  /**
   * If the value of the type attribute is `text`, `email`, `search`, `password`, `tel`, or `url`, this attribute specifies the minimum number of characters that the user can enter.
   */
  @Prop() minlength?: number;

  /**
   * The name of the control, which is submitted with the form data.
   */
  @Prop() name?: string = this.inputId;

  /**
   * Instructional text that shows before the input has a value.
   */
  @Prop() placeholder?: string | null;

  /**
   * If `true`, the user cannot modify the value.
   */
  @Prop({
    reflect: true,
  })
  readonly? = false;

  /**
   * If `true`, the user must fill in a value before submitting a form.
   */
  @Prop() required? = false;

  /**
   * If `true`, the requirement text is shown. It depends on required property. If required is true, requirement text will be "(povinné)", otherwise it displays "(nepovinné)"
   */
  @Prop() showRequirementText? = false;

  /**
   * If `true`, the element will have its spelling and grammar checked.
   */
  @Prop() spellcheck? = false;

  /**
   * The visible width of the text control, in average character widths. If it is specified, it must be a positive integer.
   */
  @Prop() cols?: number;

  /**
   * The number of visible text lines for the control.
   */
  @Prop() rows?: number = 3;

  /**
   * Indicates how the control wraps text.
   */
  @Prop() wrap?: 'hard' | 'soft' | 'off';

  /**
   * The value of the textarea.
   */
  @Prop({ mutable: true }) value?: string | null = '';

  /**
   * The label text of the textarea.
   */
  @Prop() label?: string;

  /**
   * Update the native input element when the value changes
   */
  @Watch('value')
  protected valueChanged() {
    const textarea = this.textarea;
    const value = this.getValue();

    this.internals.setFormValue(value);

    if (textarea && textarea.value !== value) {
      textarea.value = value;
    }
  }

  /**
   * Update state when disabled attribute has changed.
   */
  @Watch('disabled')
  protected disabledChanged(value: boolean) {
    this.internals.ariaDisabled = value.toString();
  }

  /**
   * Emitted when the input value has changed.
   */
  @Event() o2Change!: EventEmitter<any>;

  /**
   * Emitted when a keyboard input occurred.
   */
  @Event() o2Input!: EventEmitter<InputEvent>;

  /**
   * Emitted when the input loses focus.
   */
  @Event() o2Blur!: EventEmitter<FocusEvent>;

  /**
   * Emitted when the input has focus.
   */
  @Event() o2Focus!: EventEmitter<FocusEvent>;

  /**
   * Sets focus on the native `textarea` in `ion-textarea`. Use this method instead of the global
   * `textarea.focus()`.
   */
  @Method()
  async setFocus() {
    if (this.textarea) {
      this.textarea.focus();
    }
  }

  /**
   * Sets blur on the native `textarea` in `ion-textarea`. Use this method instead of the global
   * `textarea.blur()`.
   * @internal
   */
  @Method()
  async setBlur() {
    if (this.textarea) {
      this.textarea.blur();
    }
  }

  private getValue(): string {
    return this.value || '';
  }

  // TODO: change emit whole event to emit only value, same as o2Change
  private onInput = (ev: InputEvent) => {
    if (this.textarea) {
      this.value = this.textarea.value;
    }
    this.o2Input.emit(ev as InputEvent);
  };

  private onChange = (ev: InputEvent) => {
    this.value = (ev.target as HTMLInputElement).value;

    if (this.textarea) {
      this.value = this.textarea.value;
      this.internals.setFormValue(this.value);
    }
    this.o2Change.emit({ value: this.value == null ? this.value : this.value.toString() });
  };

  private onBlur = event => {
    event.stopPropagation();
    this.o2Blur.emit();
  };

  private onFocus = event => {
    event.stopPropagation();
    this.o2Focus.emit();
  };

  connectedCallback() {
    this.hasLabelSlot = hasSlot(this.el as HTMLElement, '[slot="label"]');
  }

  componentWillLoad() {
    this.internals.form && this.internals.setFormValue(this.value);
    this.internals.ariaReadOnly = this.readonly.toString();
    this.internals.ariaDisabled = this.disabled.toString();
    this.internals.ariaRequired = this.readonly.toString();
    this.internals.role = 'textbox';
  }

  render() {
    const value = this.getValue();
    const hasLabel = !!this.label || this.hasLabelSlot;

    return (
      <Host>
        <div class={`o2-input-group ${this.disabled ? 'o2-input-group--disabled' : ''} ${this.readonly ? 'o2-input-group--readonly' : ''}`} part="input-group">
          {hasLabel && (
            <label class={`o2-label ${this.error ? 'o2-label--error' : ''} ${this.warning ? 'o2-label--warning' : ''}`} htmlFor={this.inputId} part="label">
              <span class="o2-label__text" title={this.label}>
                {this.label && this.label}
                {this.hasLabelSlot && <slot name="label"></slot>}
              </span>
              {this.showRequirementText && <span class="o2-label__requirement"> {this.required ? 'Povinné' : 'Nepovinné'}</span>}
            </label>
          )}

          <div class="o2-input-container">
            <textarea
              ref={el => (this.textarea = el)}
              id={this.inputId}
              class={`o2-input ${this.error ? 'o2-input--error' : ''} ${this.warning ? 'o2-input--warning' : ''}`}
              aria-labelledby={this.inputId}
              autoCapitalize={this.autocapitalize}
              autoFocus={this.autofocus}
              enterKeyHint={this.enterkeyhint}
              inputMode={this.inputmode}
              disabled={this.disabled}
              maxLength={this.maxlength}
              minLength={this.minlength}
              name={this.name}
              placeholder={this.placeholder || ''}
              readOnly={this.readonly}
              required={this.required}
              spellcheck={this.spellcheck}
              cols={this.cols}
              rows={this.rows}
              wrap={this.wrap}
              onInput={this.onInput}
              onBlur={this.onBlur}
              onChange={this.onChange}
              onFocus={this.onFocus}
              part="textarea"
              role="textbox"
            >
              {value}
            </textarea>
          </div>
        </div>
      </Host>
    );
  }
}

let textareaIds = 0;
